const CLONE_SUFFIX = ' - copia';
const NAME_MAX_LENGTH = 255;

const ROOT_OMIT_FIELDS = [
    'created_at',
    'updated_at',
    'deleted_at',
    'full_name',
    'last_sync_at',
    'last_sync_error',
];

/**
 * When adding a new form collection, register its handler in CLONE_CONFIG.collections.
 */
const CLONE_CONFIG = {
    collections: {
        product_attributes: {
            strip: ['id', 'product_id'],
        },
        product_variations: {
            strip: ['id', 'product_id'],
            fields: {
                sku: () => null,
            },
        },
        product_media: {
            transform: cloneProductMediaItem,
        },
        stores: {
            transform: cloneStorePivot,
        },
    },
};

export function buildClonedName(name = '') {
    const maxBaseLength = NAME_MAX_LENGTH - CLONE_SUFFIX.length;
    const truncatedName = name.length > maxBaseLength
        ? name.slice(0, maxBaseLength).trimEnd()
        : name;

    return `${truncatedName}${CLONE_SUFFIX}`;
}

function cloneStorePivot(store) {
    return {
        id: store.id ?? store.store_id ?? null,
        slug: store.slug ?? null,
        menu_order: store.menu_order ?? store.pivot?.menu_order ?? null,
        tax_status: store.tax_status ?? store.pivot?.tax_status ?? null,
        tax_class: store.tax_class ?? store.pivot?.tax_class ?? null,
        manage_stock: store.manage_stock ?? store.pivot?.manage_stock ?? null,
        stock_quantity: store.stock_quantity ?? store.pivot?.stock_quantity ?? null,
        stock_status: store.stock_status ?? store.pivot?.stock_status ?? null,
        backorders: store.backorders ?? store.pivot?.backorders ?? null,
        date_on_sale_from: store.date_on_sale_from ?? store.pivot?.date_on_sale_from ?? null,
        date_on_sale_from_gmt: store.date_on_sale_from_gmt ?? store.pivot?.date_on_sale_from_gmt ?? null,
        date_on_sale_to: store.date_on_sale_to ?? store.pivot?.date_on_sale_to ?? null,
        date_on_sale_to_gmt: store.date_on_sale_to_gmt ?? store.pivot?.date_on_sale_to_gmt ?? null,
    };
}

function cloneProductMediaItem(media) {
    return {
        id: media.id,
        order: media.order ?? media.pivot?.order ?? null,
        url: media.url,
        name: media.name,
    };
}

function stripFields(item, fields = []) {
    const cloned = { ...item };

    fields.forEach((field) => {
        delete cloned[field];
    });

    return cloned;
}

function applyFieldOverrides(item, fields = {}) {
    const cloned = { ...item };

    Object.entries(fields).forEach(([field, resolver]) => {
        cloned[field] = typeof resolver === 'function' ? resolver(cloned[field], cloned) : resolver;
    });

    return cloned;
}

function cloneCollectionItems(items, config = {}) {
    if (!Array.isArray(items)) {
        return items;
    }

    return items.map((item) => {
        let cloned = { ...item };

        if (config.strip?.length) {
            cloned = stripFields(cloned, config.strip);
        }

        if (config.fields) {
            cloned = applyFieldOverrides(cloned, config.fields);
        }

        if (typeof config.transform === 'function') {
            cloned = config.transform(cloned);
        }

        return cloned;
    });
}

function applyRootCloneTransforms(product) {
    const cloned = { ...product };

    cloned.id = null;
    cloned.slug = null;
    cloned.sku = null;
    cloned.remote_product_id = null;
    cloned.parent_product_id = null;
    cloned.status = 'draft';
    cloned.featured = false;
    cloned.name = buildClonedName(cloned.name);

    ROOT_OMIT_FIELDS.forEach((field) => {
        delete cloned[field];
    });

    return cloned;
}

/**
 * Prepares a product payload for cloning after form wrapper transforms.
 */
export function prepareProductClone(product) {
    const cloned = applyRootCloneTransforms(product);

    Object.entries(CLONE_CONFIG.collections).forEach(([collection, config]) => {
        if (cloned[collection] !== undefined) {
            cloned[collection] = cloneCollectionItems(cloned[collection], config);
        }
    });

    return cloned;
}
